import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from './useApi';
import {
  ApiNotificationsResponse,
  ApiNotificationPatchRequest,
  ApiListNotificationsFilter,
  ApiNotificationFilters,
  ApiNotification,
} from '@repo/types';
import { useEnabled } from './useEnabled';

export function useListNotifications(filter: ApiListNotificationsFilter) {
  const api = useApi();
  const enabled = useEnabled();

  const listNotifications = async () => {
    const response = await api.get(`/notifications?show=${filter}`);
    const notificationsResponse =
      (await response?.json()) as ApiNotificationsResponse;
    return notificationsResponse.notifications;
  };

  return useQuery(['notifications', filter], listNotifications, {
    refetchInterval: 30000,
    enabled,
  });
}

function belongsToFilter(filter: ApiListNotificationsFilter, read: boolean) {
  if (filter === 'all') return true;
  return filter === 'read' ? read : !read;
}

function useOptimisticNotificationsMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<void>,
  updateList: (
    current: ApiNotification[] | undefined,
    filter: ApiListNotificationsFilter,
    variables: TVariables
  ) => ApiNotification[] | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: async (variables: TVariables) => {
      await queryClient.cancelQueries('notifications');

      const previousLists = ApiNotificationFilters.map(
        (filter) =>
          [
            filter,
            queryClient.getQueryData<ApiNotification[]>([
              'notifications',
              filter,
            ]),
          ] as const
      );

      ApiNotificationFilters.forEach((filter) => {
        queryClient.setQueryData<ApiNotification[] | undefined>(
          ['notifications', filter],
          (current) => updateList(current, filter, variables)
        );
      });

      return { previousLists };
    },
    onError: (_error, _variables, context) => {
      context?.previousLists.forEach(([filter, notifications]) => {
        queryClient.setQueryData(['notifications', filter], notifications);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries('notifications');
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const api = useApi();

  const markAllAsRead = async () => {
    await api.post('/notifications/read-all');
  };

  return useOptimisticNotificationsMutation<void>(
    markAllAsRead,
    (current, filter) =>
      belongsToFilter(filter, true)
        ? current?.map((notification) => ({ ...notification, read: true }))
        : []
  );
}

export function usePatchNotification() {
  const api = useApi();

  const updateNotification = async ({
    id,
    patchRequest,
  }: {
    id: string;
    patchRequest: ApiNotificationPatchRequest;
  }) => {
    await api.patch(`/notifications/${id}`, patchRequest);
  };

  return useOptimisticNotificationsMutation(
    updateNotification,
    (current, filter, { id, patchRequest }) =>
      current?.flatMap((notification) => {
        if (notification.id !== id) return [notification];
        if (!belongsToFilter(filter, patchRequest.read)) return [];
        return [{ ...notification, read: patchRequest.read }];
      })
  );
}
